import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { findByTurmaAnoLetivo } from "../Professor/api-integral-professor";
import { findByIntegralProfessorCodigo } from "../Presenca/api-integral-presenca";
import { findByIntegralPresencaCodigo, save } from "./api-integral-rotina";
import { dateFormat, dayOfWeekNameByDate } from "../../../util/DateUtil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { success } from "../../../util/ToastUtil";
import Container from "../../UI/Container";
import Loading from "../../UI/Loading";
import IntegralProfessorTable from "../Professor/IntegralProfessorTable";
import IntegralPresencaTable from "../Presenca/IntegralPresencaTable";
import IntegralRotinaForm from "./IntegralRotinaForm";
import IntegralRotinaTable from "./IntegralRotinaTable";
import { sintomasDe } from "./rotinaUtil";

const EMPTY_PAGE = { content: [], size: 0, totalElements: 0, number: 0 };

const porAluno = (a, b) =>
  (a.alunoTurma?.numeroChamada ?? 0) - (b.alunoTurma?.numeroChamada ?? 0) ||
  (a.alunoTurma?.aluno?.nome ?? "").localeCompare(b.alunoTurma?.aluno?.nome ?? "");

const Carregando = ({ description }) => (
  <Box py={6}>
    <Loading description={description} />
  </Box>
);

const IntegralRotinaContainer = () => {
  const [listaIP, setListaIP] = useState([]);
  const [isLoadingIP, setIsLoadingIP] = useState(true);
  const [integralProfessor, setIntegralProfessor] = useState(null);
  const [listaPresenca, setListaPresenca] = useState(EMPTY_PAGE);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoadingP, setIsLoadingP] = useState(false);
  const [integralPresenca, setIntegralPresenca] = useState(null);
  const [listaIR, setListaIR] = useState([]);
  const [isLoadingIR, setIsLoadingIR] = useState(false);
  const [editando, setEditando] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const codIP = integralProfessor?.codigo;
  const codPresenca = integralPresenca?.codigo;

  useEffect(() => {
    findByTurmaAnoLetivo()
      .then((data) => {
        if (!data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar as turmas do integral.");
        } else if (Array.isArray(data)) {
          setListaIP(data);
        }
      })
      .finally(() => setIsLoadingIP(false));
  }, []);

  useEffect(() => {
    if (!codIP) {
      setListaPresenca(EMPTY_PAGE);
      return;
    }
    let active = true;
    setIsLoadingP(true);
    findByIntegralProfessorCodigo(codIP, pageNumber)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar os dias registrados.");
        } else {
          setListaPresenca({ ...EMPTY_PAGE, ...data });
        }
      })
      .finally(() => {
        if (active) setIsLoadingP(false);
      });
    return () => {
      active = false;
    };
  }, [codIP, pageNumber]);

  useEffect(() => {
    setListaIR([]);
    if (!codPresenca) return;
    let active = true;
    setIsLoadingIR(true);
    findByIntegralPresencaCodigo(codPresenca)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message || "Não foi possível carregar a rotina do dia.");
        } else if (Array.isArray(data)) {
          setListaIR([...data].sort(porAluno));
        }
      })
      .finally(() => {
        if (active) setIsLoadingIR(false);
      });
    return () => {
      active = false;
    };
  }, [codPresenca]);

  const handleSelectIP = (ip) => {
    setIntegralProfessor(ip);
    setIntegralPresenca(null);
    setPageNumber(0);
  };

  const handleSave = (persist) => {
    setIsSaving(true);
    save(persist)
      .then((data) => {
        if (!data || data.error) {
          errorMessage(data?.message || "Não foi possível salvar a rotina.");
          return;
        }
        setListaIR((prevState) =>
          prevState.some((ir) => ir.codigo === data.codigo)
            ? prevState.map((ir) => (ir.codigo === data.codigo ? data : ir))
            : [...prevState, data].sort(porAluno),
        );
        setEditando(null);
        success(`Rotina de ${data.alunoTurma?.aluno?.nome ?? "aluno"} salva.`);
      })
      .finally(() => setIsSaving(false));
  };

  const voltar = integralPresenca
    ? { title: "Voltar para os dias", onClick: () => setIntegralPresenca(null) }
    : integralProfessor
      ? { title: "Trocar turma", onClick: () => handleSelectIP(null) }
      : null;

  const subtitulo = integralPresenca
    ? `${dayOfWeekNameByDate(integralPresenca.data)} · ${integralProfessor.turma?.descricao}`
    : integralProfessor
      ? `${integralProfessor.turma?.descricao} · Professor: ${integralProfessor.usuario?.nome ?? "--"}`
      : "Registre a alimentação, o descanso e a saúde dos alunos do integral.";

  const comSintomas = listaIR.filter((ir) => sintomasDe(ir).length > 0).length;

  const renderContent = () => {
    if (isLoadingIP) return <Carregando description="Carregando turmas do integral..." />;
    if (!integralProfessor) {
      return (
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Selecione uma turma
          </Typography>
          <IntegralProfessorTable listaIP={listaIP} setIntegralProfessor={handleSelectIP} />
        </Box>
      );
    }
    if (!integralPresenca) {
      if (isLoadingP) return <Carregando description="Carregando dias registrados..." />;
      return (
        <Box>
          <Typography variant="subtitle2" mb={1}>
            Selecione o dia
          </Typography>
          <IntegralPresencaTable
            listaPresenca={listaPresenca}
            setIntegralPresenca={setIntegralPresenca}
            handleChangePage={(event, newPage) => setPageNumber(newPage)}
          />
        </Box>
      );
    }
    if (isLoadingIR) return <Carregando description="Carregando rotina do dia..." />;
    return (
      <Stack spacing={1.5}>
        {listaIR.length > 0 && (
          <Stack direction="row" spacing={1}>
            <Chip size="small" variant="outlined" label={`${listaIR.length} alunos`} />
            <Chip
              size="small"
              variant="outlined"
              color={comSintomas > 0 ? "error" : "default"}
              label={`${comSintomas} com sintomas`}
            />
          </Stack>
        )}
        <IntegralRotinaTable listaIR={listaIR} onSelect={setEditando} />
      </Stack>
    );
  };

  return (
    <Container>
      <IntegralRotinaForm
        integralRotina={editando}
        onClose={() => setEditando(null)}
        onSave={handleSave}
        isSaving={isSaving}
      />

      <Stack direction="row" spacing={1} alignItems="center" mb={3}>
        {voltar ? (
          <Tooltip title={voltar.title}>
            <IconButton edge="start" onClick={voltar.onClick}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Menu Integral">
            <IconButton edge="start" component={Link} to="/integral">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box minWidth={0}>
          <Typography variant="h6" noWrap>
            {integralPresenca ? `Rotina de ${dateFormat(integralPresenca.data)}` : "Rotina Integral"}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {subtitulo}
          </Typography>
        </Box>
      </Stack>

      {renderContent()}
    </Container>
  );
};

export default IntegralRotinaContainer;
